import { colors } from "@fraction/shared";
import _ from "lodash";
import { Loader, LogOutIcon, MoonIcon, SunIcon, User2Icon, UserRoundPenIcon } from "lucide-react";
import { useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuthUser, useLogout } from "src/auth/useAuth";
import AccountDropdownButton from "src/components/AccountDropdownButton";
import { VersionBadge } from "src/components/VersionBadge";
import { useAccountViewPreference } from "src/hooks/useAccountViewPreference";
import { Logger } from "src/log";
import { useThemeStore } from "src/stores/themeStore";
import Dropdown from "../Dropdown";
import { Button } from "../ui/button";
import { DocumentFarmStatus } from "./DocumentFarmStatus";

const logger = new Logger("components.AccountDropdown");

const selectInitials = (firstName?: string, lastName?: string, fullName?: string, email?: string) => {
  if ((!firstName || !lastName) && !fullName && !email) {
    logger.warn("Missing user's first or last name");
    return <User2Icon color={colors.palette.GREEN_800} />;
  }
  if (firstName && lastName) {
    return firstName[0].toUpperCase() + lastName[0].toUpperCase();
  }
  if (fullName) {
    const split = fullName?.split(" ");
    if (split?.length === 1) {
      return split[0][0].toUpperCase();
    }
    return (split?.[0][0].toUpperCase() || "") + (split?.[1][0].toUpperCase() || "");
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
};

export const AccountDropdown = () => {
  const { logout, isLoggingOut } = useLogout();
  const { user } = useAuthUser();
  const { choices, preference, setPreference, isLoading } = useAccountViewPreference();
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const isDark = theme === "dark";
  const onToggleTheme = useCallback(() => toggleTheme(), [toggleTheme]);

  const onClickLogout = useCallback(() => {
    logout?.();
  }, [logout]);

  const firstName = user?.firstName;
  const lastName = user?.lastName;

  return (
    <Dropdown
      trigger={
        <AccountDropdownButton initials={selectInitials(firstName, lastName, user?.fullName, user?.email)} />
      }
      align="right"
      className="-translate-x-[26px] translate-y-2.5"
      triggerClassName="ml-auto"
    >
      <div className="max-w-xl min-w-[320px] flex flex-col gap-y-1 p-2">
        {firstName ? (
          <div className="flex flex-row items-center px-3 py-2 border-b border-border">
            <div>
              <p className="font-semibold leading-[27px] text-foreground">Hello, {firstName}</p>
              {user?.email ? <p className="text-xs text-muted-foreground">{user.email}</p> : null}
              {choices?.length ? (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Viewing as <b>{preference}</b>
                  {preference === "lender" && user?.lenderAccount?.lenderId
                    ? ` (${_.startCase(user.lenderAccount.lenderId)})`
                    : ""}
                  {preference === "desk-agent" && user?.underwritingDeskMember?.underwritingDesk?.name
                    ? ` (${_.startCase(user?.underwritingDeskMember?.underwritingDesk?.name)})`
                    : ""}
                </p>
              ) : null}
            </div>
            {isLoading ? <Loader height={20} className="ml-2 text-muted-foreground animate-spin" /> : null}
          </div>
        ) : null}
        {choices?.length > 1
          ? choices
              .filter((choice) => choice !== preference)
              .map((choice) => (
                <Button
                  key={choice}
                  onClick={() => setPreference(choice)}
                  loading={isLoggingOut}
                  variant="outline"
                  size="sm"
                >
                  View as {choice}
                </Button>
              ))
          : null}
        <Link to="/profile" className="w-full">
          <Button size="sm" variant="default" className="w-full">
            <UserRoundPenIcon className="w-3 h-3 mb-0.5 mr-1 inline" /> Edit profile
          </Button>
        </Link>
        <Button size="sm" onClick={onToggleTheme} variant="default">
          {isDark ? (
            <SunIcon className="w-3 h-3 mb-0.5 mr-1 inline" />
          ) : (
            <MoonIcon className="w-3 h-3 mb-0.5 mr-1 inline" />
          )}{" "}
          {isDark ? "Light mode" : "Dark mode"}
        </Button>
        {preference === "employee" ? <DocumentFarmStatus /> : null}
        <VersionBadge />
        <Button size="sm" onClick={onClickLogout} loading={isLoggingOut} variant="default">
          <LogOutIcon className="w-3 h-3 mb-0.5 mr-1 inline" /> Log out
        </Button>
      </div>
    </Dropdown>
  );
};
